import apiClient from './api';

// API Key 类型
export interface APIKey {
  id: string;
  userId: string;
  name: string;
  keyPrefix: string;
  status: 'active' | 'inactive' | 'revoked';
  lastUsedAt?: string;
  expiresAt?: string;
  rateLimit: number;
  createdAt: string;
  updatedAt: string;
}

// Webhook 类型
export interface Webhook {
  id: string;
  userId: string;
  workflowId: string;
  name: string;
  urlToken: string;
  secret?: string;
  status: 'active' | 'inactive' | 'error';
  triggerType: 'incoming' | 'outgoing';
  method: string;
  contentType: string;
  inputMapping?: string;
  outputUrl?: string;
  retryCount: number;
  timeout: number;
  ipWhitelist?: string;
  callCount: number;
  lastCalledAt?: string;
  lastError?: string;
  createdAt: string;
  updatedAt: string;
}

// Webhook 日志类型
export interface WebhookLog {
  id: string;
  webhookId: string;
  userId: string;
  workflowId: string;
  runId?: string;
  direction: 'incoming' | 'outgoing';
  method: string;
  url: string;
  headers?: string;
  requestBody?: string;
  responseBody?: string;
  statusCode: number;
  status: 'success' | 'failed' | 'pending';
  error?: string;
  duration: number;
  ipAddress?: string;
  createdAt: string;
}

// 创建 API Key 请求
export interface CreateAPIKeyRequest {
  name: string;
  expiresAt?: string;
  rateLimit?: number;
}

// 创建 API Key 响应
export interface CreateAPIKeyResponse {
  key: APIKey;
  fullKey: string;
}

// 创建 Webhook 请求
export interface CreateWebhookRequest {
  name: string;
  workflowId: string;
  triggerType?: 'incoming' | 'outgoing';
  method?: string;
  contentType?: string;
  inputMapping?: string;
  outputUrl?: string;
  retryCount?: number;
  timeout?: number;
  ipWhitelist?: string;
}

// Webhook 详情响应
export interface WebhookDetailResponse {
  webhook: Webhook;
  webhookUrl: string;
}

// Developer API
export const developerApi = {
  // ========== API Keys ==========
  
  // 获取 API Key 列表
  listAPIKeys: () =>
    apiClient.get<{ keys: APIKey[] }>('/developer/api-keys'),

  // 创建 API Key
  createAPIKey: (data: CreateAPIKeyRequest) =>
    apiClient.post<CreateAPIKeyResponse>('/developer/api-keys', data),

  // 删除 API Key
  deleteAPIKey: (id: string) =>
    apiClient.delete<void>(`/developer/api-keys/${id}`),

  // 更新 API Key 状态
  updateAPIKeyStatus: (id: string, status: 'active' | 'inactive' | 'revoked') =>
    apiClient.patch<void>(`/developer/api-keys/${id}/status`, { status }),

  // ========== Webhooks ==========
  
  // 获取 Webhook 列表
  listWebhooks: (workflowId?: string) =>
    apiClient.get<{ webhooks: Webhook[]; baseUrl: string }>('/developer/webhooks', {
      params: workflowId ? { workflowId } : undefined,
    }),

  // 创建 Webhook
  createWebhook: (data: CreateWebhookRequest) =>
    apiClient.post<WebhookDetailResponse>('/developer/webhooks', data),

  // 获取 Webhook 详情
  getWebhook: (id: string) =>
    apiClient.get<WebhookDetailResponse>(`/developer/webhooks/${id}`),

  // 更新 Webhook
  updateWebhook: (id: string, data: Partial<CreateWebhookRequest>) =>
    apiClient.put<void>(`/developer/webhooks/${id}`, data),

  // 删除 Webhook
  deleteWebhook: (id: string) =>
    apiClient.delete<void>(`/developer/webhooks/${id}`),

  // 重新生成 Webhook Secret
  regenerateWebhookSecret: (id: string) =>
    apiClient.post<{ secret: string }>(`/developer/webhooks/${id}/regenerate-secret`),

  // ========== Logs ==========
  
  // 获取 Webhook 日志
  getWebhookLogs: (webhookId: string, limit?: number) =>
    apiClient.get<{ logs: WebhookLog[] }>(`/developer/webhooks/${webhookId}/logs`, {
      params: limit ? { limit } : undefined,
    }),

  // 获取所有日志
  getAllLogs: (limit?: number) =>
    apiClient.get<{ logs: WebhookLog[] }>('/developer/logs', {
      params: limit ? { limit } : undefined,
    }),
};

// 生成 Webhook URL
export const generateWebhookURL = (baseUrl: string, urlToken: string): string => {
  const cleanBaseUrl = baseUrl.replace(/\/$/, '');
  return `${cleanBaseUrl}/webhook/flow/${urlToken}`;
};

// 生成 cURL 示例
export const generateCurlExample = (webhookUrl: string, secret?: string): string => {
  const payload = JSON.stringify({
    orders: [
      { sku: 'A1001', qty: 10 },
      { sku: 'B2033', qty: 5 },
    ],
  }, null, 2);

  let curl = `curl -X POST "${webhookUrl}" \\
  -H "Content-Type: application/json"`;

  if (secret) {
    curl += ` \\
  -H "X-Signature: YOUR_SIGNATURE"`;
  }

  curl += ` \\
  -d '${payload}'`;

  return curl;
};

// 生成 Python 示例
export const generatePythonExample = (webhookUrl: string, secret?: string): string => {
  let code = `import requests
import json
import hmac
import hashlib

url = "${webhookUrl}"

payload = {
    "orders": [
        {"sku": "A1001", "qty": 10},
        {"sku": "B2033", "qty": 5}
    ]
}

headers = {
    "Content-Type": "application/json"
}`;

  if (secret) {
    code += `

# 生成 HMAC 签名
secret = "${secret}"
payload_json = json.dumps(payload, separators=(',', ':'))
signature = hmac.new(
    secret.encode(),
    payload_json.encode(),
    hashlib.sha256
).hexdigest()
headers["X-Signature"] = signature`;
  }

  code += `

response = requests.post(url, json=payload, headers=headers)
print(response.json())`;

  return code;
};

// 生成 Node.js 示例
export const generateNodeExample = (webhookUrl: string, secret?: string): string => {
  let code = `const axios = require('axios');

const url = '${webhookUrl}';

const payload = {
  orders: [
    { sku: 'A1001', qty: 10 },
    { sku: 'B2033', qty: 5 }
  ]
};

const headers = {
  'Content-Type': 'application/json'
};`;

  if (secret) {
    code += `

// 生成 HMAC 签名
const crypto = require('crypto');
const secret = '${secret}';
const payloadJson = JSON.stringify(payload);
const signature = crypto
  .createHmac('sha256', secret)
  .update(payloadJson)
  .digest('hex');
headers['X-Signature'] = signature;`;
  }

  code += `

axios.post(url, payload, { headers })
  .then(response => console.log(response.data))
  .catch(error => console.error(error));`;

  return code;
};

export default developerApi;
